import { defineMessages } from 'react-intl';

import {
	getEmojiPublishedFromProperties,
	getEmojiDraftFromProperties,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import {
	getEmojiFromTitle,
	removeEmojiFromTitle,
} from '@confluence/emoji-title/entry-points/helpers.ts';
import { fg } from '@confluence/feature-gating';

type ContentString = string | null | undefined;

type EmojiContent = Parameters<typeof getEmojiPublishedFromProperties>[0] & {
	title?: ContentString;
};

const getEmoji = (content: EmojiContent) => {
	const emojiPropertyPublished = getEmojiPublishedFromProperties(content);
	const emojiPropertyDraft = getEmojiDraftFromProperties(content);
	const emoji = emojiPropertyPublished || emojiPropertyDraft || getEmojiFromTitle(content.title);

	return {
		hasEmojiProperty: Boolean(emojiPropertyPublished || emojiPropertyDraft),
		emoji,
	};
};

const isEmojiSupportedType = (type: ContentString): boolean =>
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	!!type && (type !== 'whiteboard' || fg('confluence_frontend_whiteboard_emoji_titles'));

const getTitle = (title: ContentString, hasEmojiProperty: boolean | undefined): ContentString =>
	hasEmojiProperty ? title : removeEmojiFromTitle(title);

export const getContentEmojiAndTitle = (
	content: EmojiContent & { type?: ContentString },
): { emoji: string | undefined; title: ContentString } => {
	if (isEmojiSupportedType(content.type)) {
		const { emoji, hasEmojiProperty } = getEmoji(content);
		return { emoji, title: getTitle(content.title, hasEmojiProperty) };
	}
	return { emoji: undefined, title: content.title };
};

export const i18n: {
	untitled: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	untitled: {
		id: 'side-navigation.content-menu.untitled',
		defaultMessage: 'Untitled',
		description: 'Title shown in global navigation content menu when the content has no title',
	},
});
